import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { IMAGES } from "@/data/content";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.6, delay: delay / 1000, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

export const MaskedLine = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
      <motion.span
        className={`block ${className}`}
        initial={reduce ? false : { y: "115%" }}
        animate={reduce ? undefined : { y: "0%" }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const CountUp = ({ end, suffix = "", duration = 1600, testId }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setValue(end);
      return;
    }
    const run = () => {
      if (started.current) return;
      started.current = true;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(Math.round(end * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          run();
          obs.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} data-testid={testId}>
      {value.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
};

export const Eyebrow = ({ children, dark = false }) => (
  <p className={`eyebrow ${dark ? "eyebrow-on-dark" : "eyebrow-on-light"} mb-4`}>{children}</p>
);

export const PrimaryLink = ({ to, children, testId, className = "" }) => (
  <Link to={to} className={`btn btn-primary ${className}`} data-testid={testId}>
    {children}
    <ArrowRight size={17} aria-hidden="true" />
  </Link>
);

export const OutlineLink = ({ to, children, testId, className = "" }) => (
  <Link to={to} className={`btn btn-outline ${className}`} data-testid={testId}>
    {children}
    <ArrowRight size={17} aria-hidden="true" />
  </Link>
);

export const ArrowLink = ({ to, children, testId, className = "" }) => (
  <Link to={to} className={`link-arrow min-h-[44px] ${className}`} data-testid={testId}>
    {children}
    <ArrowRight size={16} aria-hidden="true" />
  </Link>
);

export const ChakraWatermark = ({ opacity = 0.07, className = "" }) => (
  <img
    src="/assets/dhamma-chakra.png"
    alt=""
    aria-hidden="true"
    data-testid="chakra-watermark"
    className={`chakra-watermark ${className}`}
    style={{ opacity }}
  />
);

export const PageHero = ({ eyebrow, title, intro, testId }) => (
  <section className="relative overflow-hidden text-white" data-testid={testId} style={{ backgroundColor: "#17120D" }}>
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse at 82% 30%, rgba(201, 138, 46,0.14) 0%, rgba(23, 18, 13,0) 55%), linear-gradient(180deg, rgba(23, 18, 13,1) 0%, rgba(23, 18, 13,1) 100%)",
      }}
      aria-hidden="true"
    />
    <div
      className="absolute inset-y-0 right-0 w-1/2 hidden lg:block"
      style={{
        background:
          "radial-gradient(circle at 70% 50%, rgba(226, 170, 85,0.08) 0%, rgba(23, 18, 13,0) 60%)",
      }}
      aria-hidden="true"
    />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
      <Reveal>
        <Eyebrow dark>{eyebrow}</Eyebrow>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white max-w-3xl">{title}</h1>
        {intro && <p className="mt-6 text-base lg:text-lg text-[rgba(245,239,228,0.82)] max-w-2xl leading-relaxed">{intro}</p>}
      </Reveal>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-px bg-[rgba(201,138,46,0.14)]" aria-hidden="true" />
  </section>
);
